import axios from 'axios'
import { useCallback, useEffect, useState } from 'react'
import config from 'config'
import { useTranslation } from 'react-i18next'
import logger from 'logger'
import { TABLE_ROWS_PER_PAGE } from 'lib/tableLib'

import useSnackbar from 'components/organisms/Snackbar/useSnackbar'

const useEntityData = ({
  newPage,
  limit = TABLE_ROWS_PER_PAGE,
  entity,
  filters,
  sort,
  direction,
} = {}) => {
  const [state, setState] = useState({
    fetchedFirstTime: false,
    isFetching: false,
    isFetchingOtherPages: false,
    page: 0,
    rowsPerPage: limit,
    items: [],
    total: 0,
    others: {},
    error: null,
  })
  const { showSnackbarMessage } = useSnackbar()
  const { t } = useTranslation()

  const setStartFetching = useCallback(() => {
    setState((s) => ({
      ...s,
      isFetching: !s.fetchedFirstTime,
      isFetchingOtherPages: s.fetchedFirstTime,
    }))
  }, [])

  const setSuccessFetching = useCallback(
    (items, total, others, page, rowsPerPage) => {
      setState((s) => ({
        ...s,
        page,
        rowsPerPage,
        items,
        total,
        others,
        fetchedFirstTime: true,
        isFetching: false,
        isFetchingOtherPages: false,
        error: null,
      }))
    },
    []
  )

  const fetchDataWithLimit = useCallback(
    async (route, customLimit, customPage) => {
      if (!route && !entity) {
        return
      }
      try {
        const result = await axios.get(
          `${config.apiBaseUrl}${entity && !route ? entity : route}`,
          {
            params: {
              page: customPage,
              limit: customLimit,
              filters,
              sort,
              direction,
            },
          }
        )
        const others = { ...result.data.body }
        delete others.items
        delete others.total

        return {
          items: result.data.body.items,
          total: result.data.body.total,
          others: others,
        }
      } catch (e) {
        logger.error(`[Error] - fetching ${entity}`, e)
        setState((s) => ({
          ...s,
          fetchedFirstTime: false,
          isFetching: false,
          isFetchingOtherPages: false,
          error: e,
        }))
        showSnackbarMessage({
          severity: 'error',
          message: t('fetchError', { name: entity }),
        })
      }
    },
    [entity, filters, sort, direction, showSnackbarMessage, t]
  )

  const fetchData = useCallback(
    async (route) => {
      if (!route && !entity) {
        return
      }
      setStartFetching()

      const result = await fetchDataWithLimit(route, limit, newPage)
      if (!result) {
        return
      }
      const { items, total, others } = result
      setSuccessFetching(items, total, others, newPage, limit)
    },
    [
      entity,
      fetchDataWithLimit,
      limit,
      newPage,
      setStartFetching,
      setSuccessFetching,
    ]
  )

  const fetchAllData = useCallback(
    async (route) => {
      if (!route && !entity) {
        return
      }
      setState((s) => ({
        ...s,
        isFetchingOtherPages: true,
      }))
      const { items } = await fetchDataWithLimit(route, 999999, 0)
      setState((s) => ({
        ...s,
        fetchedFirstTime: false,
        isFetching: false,
        isFetchingOtherPages: false,
        error: null,
      }))
      return items
    },
    [entity, fetchDataWithLimit]
  )

  useEffect(() => {
    if (!entity) {
      return
    }
    fetchData()
  }, [entity, fetchData])

  return {
    page: state.page,
    rowsPerPage: state.rowsPerPage,
    items: state.items,
    total: state.total,
    others: state.others,
    isFetching: state.isFetching,
    isFetchingOtherPages: state.isFetchingOtherPages,
    refetch: fetchData,
    setEntityState: setState,
    fetchAllData,
  }
}

export default useEntityData
